import { useOrganization } from '@clerk/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';
import DocumentGenerator from 'shared/documents/DocumentGenerator';
import { PALLET_SSCC_DOCUMENT_FLAG } from 'shared/documents/palletSscc';
import { usePalletSsccEnabled } from 'src/hooks/usePalletSsccEnabled';

export default function useDocumentPreview({
  documentTemplate,
  data,
  getDocumentData,
  type,
  entityType,
  extendedData,
  generationOptions,
  entitySettings,
}: {
  documentTemplate?: any;
  data?: any;
  getDocumentData?: () => Promise<any>;
  type?: any;
  entityType?: any;
  extendedData?: any;
  generationOptions?: any;
  entitySettings?: Record<string, any>;
}) {
  const { organization } = useOrganization();
  const [logo, setLogo] = useState(null);

  /**
   * Work around for CORS fetching for rendering images
   * in documents
   */
  useEffect(() => {
    if (!organization?.imageUrl || logo) {
      return;
    }

    fetch(organization?.imageUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        setLogo(url);
      });
  }, [organization?.imageUrl, logo]);

  const flags = useFlags();
  const isPalletSsccEnabled = usePalletSsccEnabled();
  const documentSettings = flags['documentSettings'];
  const documentSettingsRef = useRef(documentSettings);

  // Only update ref when the value actually changes (deep compare)
  const settingsJson = JSON.stringify(documentSettings);
  const prevSettingsJson = useRef(settingsJson);
  if (settingsJson !== prevSettingsJson.current) {
    prevSettingsJson.current = settingsJson;
    documentSettingsRef.current = documentSettings;
  }
  const stableDocumentSettings = documentSettingsRef.current;

  const [document, setDocument] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const generateDocument = async () => {
      if (!type) return;

      const settings = { ...stableDocumentSettings, ...entitySettings };
      const documentData = data ? data : await getDocumentData();
      if (!documentData) return;

      try {
        const pages = await DocumentGenerator.generate(
          entityType,
          type,
          documentTemplate,
          documentData,
          {
            ...(extendedData ?? {}),
            orgLogo: logo,
            _settings: settings,
            [PALLET_SSCC_DOCUMENT_FLAG]: isPalletSsccEnabled,
          },
          generationOptions
        );

        if (!signal.aborted) {
          setDocument(pages);
        }
      } catch (e) {
        if (signal.aborted) {
          console.log('Document generation was aborted');
        } else {
          console.warn(e);
        }
      }
    };

    generateDocument();

    return () => {
      controller.abort(); // prevent race condition cancelling in flight-rendering
    };
  }, [
    type,
    entityType,
    data,
    documentTemplate,
    logo,
    stableDocumentSettings,
    extendedData,
    getDocumentData,
    generationOptions,
    entitySettings,
    isPalletSsccEnabled,
  ]);

  return document;
}
